import { Common, color } from '@stockbit/ui';
import { useEffect, useRef } from 'react';

import Image from 'global/Image';
import { FormHeader } from 'global/TradingModal/styled';
import If from 'global/ErrorHandlers/If';
import SearchCompany from 'global/TradingModal/components/SearchCompany';
import tradingModalStore from 'global/TradingModal/store';

import { useFliptBoolean } from 'lib/flipt/hooks';
import { FeatureFlagKeys } from 'lib/flipt/constants/flagKeys';

import { useBondsModalStore } from '../stores/useBondsModalStore';
import { FALLBACK_AVATAR_SRC } from '../constants';
import { useBondPreviewStore } from '../stores/useBondPreview';
import { TextLoadingShimmer } from '../styled';
import { useBondTransaction } from '../stores/useBondTransaction';

const { Text, Flex, Iconmoon } = Common;

interface Props {
  isSearchEnabled?: boolean;
  isLoading?: boolean;
}

export default function ModalProductDisplay({
  isSearchEnabled = false,
  isLoading = false,
}: Props) {
  const stockInputRef = useRef<HTMLInputElement>();
  const isFREligible = useFliptBoolean(FeatureFlagKeys.FR_BONDS);

  const [toggleSearch, isSearchActive, closeTransactionModal] =
    useBondsModalStore((s) => [
      s.toggleSearch,
      s.isSearchActive,
      s.closeTransactionModal,
    ]);

  const { field } = useBondPreviewStore((s) => ({
    field: s.product,
  }));

  const { initiateBuy } = useBondTransaction((s) => ({
    initiateBuy: s.initiateBuyTransaction,
  }));

  const [showTradingModal] = tradingModalStore((s) => [s.showModal]);

  const handleUpdateSymbol = (newSymbol: string, isBonds: boolean) => {
    if (!isBonds) {
      showTradingModal({
        symbol: newSymbol,
        orderType: 'BUY',
      });
    }

    if (isBonds && isFREligible) {
      initiateBuy(newSymbol);
    }

    if (isSearchActive) {
      toggleSearch();
    }

    closeTransactionModal();
  };

  useEffect(() => {
    if (isSearchActive) {
      stockInputRef.current?.focus();
    }
  }, [isSearchActive]);

  return (
    <FormHeader style={{ paddingTop: 0 }}>
      <Flex
        justify="space-between"
        width="100%"
        style={{ display: isSearchActive ? 'none' : 'flex' }}
      >
        <Flex align="flex-start">
          <Image
            src={field?.icon_url || FALLBACK_AVATAR_SRC}
            style={{ width: '40px', height: '40px', background: 'white' }}
          />
          <Flex direction="column" pl="15px">
            <Flex align="center" width="200px">
              {isLoading ? (
                <TextLoadingShimmer width="80px" height="16px" />
              ) : (
                <Text family="bold" size="16px">
                  {field?.id}
                </Text>
              )}
            </Flex>
            <Flex mt="3px">
              {isLoading ? (
                <TextLoadingShimmer width="160px" height="16px" />
              ) : (
                <Text size="14px" color={color.midgray} mr="5px">
                  {field?.name}
                </Text>
              )}
              <If condition={isSearchEnabled}>
                <Iconmoon
                  className="icon-tradesmall_pencil2"
                  size="14px"
                  hoverColor={color.highgray}
                  onClick={toggleSearch}
                />
              </If>
            </Flex>
          </Flex>
        </Flex>
      </Flex>

      <If condition={isSearchEnabled && isSearchActive}>
        <Flex width="100%">
          <SearchCompany
            ref={stockInputRef}
            isInTradingModal
            updateSymbol={handleUpdateSymbol}
          />
        </Flex>
      </If>
    </FormHeader>
  );
}
